/**
 * @file routes/__root.tsx
 * @description TanStack Router 根路由。
 *
 * 職責：
 * - 提供全局 Providers（ConfigProvider, QueryClientProvider, AuthProvider）
 * - 掛載 TanStack Router Devtools（僅 dev 模式）
 * - 所有頁面路由均為此根路由的子節點
 */

import { Outlet, createRootRoute } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { App as AntdApp, ConfigProvider } from 'antd';
import zhTW from 'antd/locale/zh_TW';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/components/auth/AuthProvider';
import { AntdAppBridge } from '@/lib/antd-app';
import { color } from '@/styles/tokens';

/**
 * TanStack Query 全局客戶端。
 * - staleTime: 30 秒，減少重複請求
 * - retry: 1，失敗後重試一次
 */
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
});

/**
 * 根路由組件。
 * 包裹全局 Provider 和 Outlet，所有子路由渲染在 Outlet 中。
 */
export const Route = createRootRoute({
  component: () => (
    <ConfigProvider
      locale={zhTW}
      theme={{
        token: {
          // 品牌主色（forest green）——与登录/忘记密码页绿色渐变、绿色链接保持一致，
          // 取自 @/styles/tokens 的 brand token，避免各处硬编码色值走散
          colorPrimary: color.brand.primary,
          colorPrimaryHover: color.brand.primaryHover,
          colorPrimaryActive: color.brand.primaryActive,
        },
      }}
    >
      {/* antd <App> 提供可消费 context 的 message/modal/notification 实例；
          AntdAppBridge 把这些实例桥接到 @/lib/antd-app 供全局静态调用。
          用默认 component（div，渲染 .ant-app）以兼容 antd 6 cssVar；
          顶层业务布局用 100vh，不依赖父级高度链，故该包裹 div 不影响布局。 */}
      <AntdApp>
        <AntdAppBridge />
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <Outlet />
          </AuthProvider>
        </QueryClientProvider>
        {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
      </AntdApp>
    </ConfigProvider>
  ),
});
